/**
 * Fetch a file-download endpoint and save the response, preferring the filename the server sends
 * in `Content-Disposition` over `fallback_filename`.
 */
export async function download_export(url: string, fallback_filename: string): Promise<void> {
  const response = await fetch(url, { credentials: "include" })
  if (!response.ok) {
    throw new Error("Export failed")
  }
  const blob = await response.blob()
  const filename = filename_from_content_disposition(response.headers.get("Content-Disposition")) ?? fallback_filename
  save_blob(blob, filename)
}

/** Like `download_export`, for endpoints that take a JSON body (e.g. a list of selected ids). */
export async function download_post_export(url: string, body: unknown, fallback_filename: string): Promise<void> {
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new Error(error_message_from_body(await response.text().catch(() => "")))
  }
  const blob = await response.blob()
  const filename = filename_from_content_disposition(response.headers.get("Content-Disposition")) ?? fallback_filename
  save_blob(blob, filename)
}

/** The server's `{"error": "..."}` message, the raw text, or a generic fallback. */
export function error_message_from_body(text: string): string {
  try {
    const parsed = JSON.parse(text)
    if (parsed && typeof parsed.error === "string") return parsed.error
  } catch {
    // not JSON
  }
  return text || "Export failed"
}

/** Trigger a browser download of an in-memory blob. */
export function save_blob(blob: Blob, filename: string): void {
  const blob_url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = blob_url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(blob_url)
}

/** Pull the `filename="..."` out of a `Content-Disposition` header, if it has one. */
export function filename_from_content_disposition(header: string | null): string | null {
  if (!header) return null
  const match = header.match(/filename="?([^"]+)"?/)
  return match ? match[1] : null
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("error_message_from_body", () => {
    it("prefers the server's error field, then the raw text", () => {
      expect(error_message_from_body('{"error":"At most 500 deductions per download (got 612)"}')).toBe(
        "At most 500 deductions per download (got 612)"
      )
      expect(error_message_from_body("Bad Gateway")).toBe("Bad Gateway")
      expect(error_message_from_body("")).toBe("Export failed")
    })
  })
  describe("filename_from_content_disposition", () => {
    it("reads a quoted filename", () => {
      expect(filename_from_content_disposition('attachment; filename="sales_by_retailer_kehe_2026-07-24.xlsx"')).toBe(
        "sales_by_retailer_kehe_2026-07-24.xlsx"
      )
    })
    it("reads an unquoted filename", () => {
      expect(filename_from_content_disposition("attachment; filename=report.xlsx")).toBe("report.xlsx")
    })
    it("falls back to null when absent", () => {
      expect(filename_from_content_disposition(null)).toBe(null)
      expect(filename_from_content_disposition("attachment")).toBe(null)
    })
  })
}
